// The rule rows of the visual audience editor: groups OR'd together, rules inside a group AND'd,
// one row of controls per rule field (`RULE_FIELDS` in `@/lib/audience-rule-fields`). Rows hold
// visual values; `resolveVisualAudienceRules` turns them into canonical API rules before any
// preview or save leaves the browser.

import { Fragment, useMemo } from 'react';
import { Icons } from '@/components/primitives';
import {
  SearchableDropdown,
} from '@/components/dropdowns';
import { RequiredField } from '@/components/required-field';
import { MultiSelect } from '@/components/multi-select';
import { PurchaseWindowPicker } from '@/components/purchase-window-picker';
import {
  useAudiences,
  usePixels,
  useProductions,
  useVenues,
} from '@/api/hooks';
import type { Audience, Pixel, Production } from '@/api/types';
import {
  PIXEL_RETARGETING_ACTIONS,
  isLegacyRangeValue,
  legacyRangeLabel,
  visualTypeFromAudienceType,
  type VisualAudienceGroup,
  type VisualAudienceRule,
  type VisualAudienceType,
} from '@/lib/audience-visual-rules';
import {
  RULE_FIELDS,
  WINDOWS,
  canAddAudienceGroup,
  defaultRuleValue,
  dropdownOptions,
  eligibleLookalikeSeeds,
  prioritizeProduction,
  productionOptionSublabel,
  ruleRecord,
  type AudienceRuleContext,
  type RuleField,
} from '@/lib/audience-rule-fields';

type RuleRecord = Record<string, unknown>;

/** A key of a rule's value record when it holds a string, else ''. */
const textOf = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The lookback windows, keeping a stored value that is not one of them selectable. */
const windowOptions = (current: unknown) =>
  dropdownOptions([...new Set([textOf(current), ...WINDOWS])].filter(Boolean));

export interface RulesEditorProps {
  type: VisualAudienceType;
  groups: VisualAudienceGroup[];
  setGroups: (groups: VisualAudienceGroup[]) => void;
  /** The campaign's production, offered first in the production pickers. */
  production?: Production | null;
  /** Reference lists; each one the caller leaves out is read here. */
  productions?: readonly Production[];
  audiences?: readonly Audience[];
  pixels?: readonly Pixel[];
  validationMessages?: Readonly<Record<string, string | undefined>>;
  validationAttempt?: number;
  /** Whether the editor prints its own "Segment rules" heading; off where the page already
   * heads the section (the audience drawer's "Rules"). */
  heading?: boolean;
}

export function RulesEditor({
  type,
  groups,
  setGroups,
  production = null,
  productions: productionsProp,
  audiences: audiencesProp,
  pixels: pixelsProp,
  validationMessages = {},
  heading = true,
  validationAttempt = 0,
}: RulesEditorProps) {
  const fields = RULE_FIELDS[type];
  const { data: fetchedProductions = [] } = useProductions();
  const { data: fetchedAudiences = [] } = useAudiences();
  const { data: fetchedPixels = [] } = usePixels();
  const { data: venues = [] } = useVenues();
  const productions = productionsProp || fetchedProductions;
  const audiences = audiencesProp || fetchedAudiences;
  const pixels = pixelsProp || fetchedPixels;
  const seedAudiences = type === 'Lookalike'
    ? eligibleLookalikeSeeds(audiences)
    : audiences;
  const productionOptions = prioritizeProduction(production, productions);
  const context: AudienceRuleContext = {
    productions: productionOptions,
    audiences,
    pixels,
    preferredProductionId: production?.id ?? null,
  };
  const venueNames = useMemo(
    () => new Map(venues.map((venue) => [venue.id, venue.name] as const)),
    [venues],
  );
  /** A record-valued rule's value, or the field's default when the stored one is not a record. */
  const recordValue = (rule: VisualAudienceRule, field: RuleField): RuleRecord =>
    ruleRecord(rule.value) ?? ruleRecord(defaultRuleValue(field, context)) ?? {};

  const updateGroup = (index: number, transform: (group: VisualAudienceGroup) => VisualAudienceGroup) => {
    setGroups(groups.map((group, groupIndex) =>
      groupIndex === index ? transform(group) : group));
  };
  const addRule = (groupIndex: number) => {
    const used = new Set(groups[groupIndex]?.rules.map((rule) => rule.field));
    const next = fields.find((field) => !used.has(field.id));
    if (!next) return;
    updateGroup(groupIndex, (group) => ({
      ...group,
      rules: [...group.rules, {
        field: next.id,
        operator: next.op,
        value: defaultRuleValue(next, context),
      }],
    }));
  };
  const removeRule = (groupIndex: number, ruleIndex: number) => {
    updateGroup(groupIndex, (group) => ({
      ...group,
      rules: group.rules.filter((_, index) => index !== ruleIndex),
    }));
  };
  const updateRule = (groupIndex: number, ruleIndex: number, patch: Partial<VisualAudienceRule>) => {
    updateGroup(groupIndex, (group) => ({
      ...group,
      rules: group.rules.map((rule, index) =>
        index === ruleIndex ? { ...rule, ...patch } : rule),
    }));
  };
  const addGroup = () => {
    const first = fields[0];
    if (!first) return;
    setGroups([...groups, {
      id: `segment-${groups.length + 1}`,
      op: 'AND',
      rules: [{
        field: first.id,
        operator: first.op,
        value: defaultRuleValue(first, context),
      }],
    }]);
  };
  const changeField = (groupIndex: number, ruleIndex: number, fieldId: string) => {
    const next = fields.find((field) => field.id === fieldId);
    if (!next) return;
    updateRule(groupIndex, ruleIndex, {
      field: next.id,
      operator: next.op,
      value: defaultRuleValue(next, context),
    });
  };

  return (
    <RequiredField
      fieldKey="audience-rules"
      message={validationMessages['audience-rules']}
      attempt={validationAttempt}
      surface
    >
      {heading && <div className="field-lbl" style={{marginBottom:8}}>Segment rules</div>}
      {groups.map((group, groupIndex) => (
        <Fragment key={group.id || groupIndex}>
          {groupIndex > 0 && (
            <div className="or-divider">
              <span className="or-line"/>
              <span className="or-pill">OR</span>
              <span className="or-line"/>
            </div>
          )}
          <RequiredField
            fieldKey={`audience-group-${groupIndex}`}
            message={validationMessages[`audience-group-${groupIndex}`]}
            attempt={validationAttempt}
            className="logic-group"
          >
            <div className="logic-group-hd">
              <span className="logic-pill">GROUP {groupIndex + 1}</span>
              <span className="tag">AND</span>
              <span className="muted" style={{fontSize:11}}>Match all of {group.rules.length} {group.rules.length === 1 ? 'rule' : 'rules'}</span>
              {groups.length > 1 && (
                <button className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}} onClick={() => setGroups(groups.filter((_, index) => index !== groupIndex))}>
                  {Icons.x} Remove
                </button>
              )}
            </div>
            {group.rules.map((rule, ruleIndex) => {
              const validationKey = `audience-rule-${groupIndex}-${ruleIndex}`;
              const validationMessage = validationMessages[validationKey];
              const field = fields.find((entry) => entry.id === rule.field);
              if (rule.field === 'unsupported' || !field) {
                const legacyKind = ruleRecord(rule.value)?.kind;
                const legacyRetargetingRule = legacyKind === 'web_visit' || legacyKind === 'web_action';
                return (
                  <RequiredField
                    key={ruleIndex}
                    fieldKey={validationKey}
                    message={validationMessage}
                    attempt={validationAttempt}
                    className="rule"
                  >
                    <div
                      className="muted"
                      style={{
                        gridColumn:'1 / -2',
                        fontSize:11.5,
                        display:'flex',
                        alignItems:'center',
                        justifyContent:'space-between',
                        gap:10,
                      }}
                    >
                      <span>
                        {legacyRetargetingRule
                          ? 'This legacy domain/event rule has no pixel selection.'
                          : type === 'Lookalike'
                            ? 'This rule is not supported for lookalikes. Remove it to continue.'
                            : 'This persisted rule cannot be edited safely.'}
                      </span>
                      {legacyRetargetingRule && type === 'Retargeting' && (
                        <button
                          className="btn btn-sm"
                          type="button"
                          onClick={() => changeField(groupIndex, ruleIndex, 'pixel_action')}
                        >
                          Replace with pixel + action
                        </button>
                      )}
                    </div>
                    <span className="x" role="button" tabIndex={0} aria-label="Remove rule" onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();removeRule(groupIndex, ruleIndex);}}} onClick={() => removeRule(groupIndex, ruleIndex)}>{Icons.x}</span>
                  </RequiredField>
                );
              }
              const fieldSelect = (
                <SearchableDropdown
                  value={rule.field}
                  onChange={(fieldId) => changeField(groupIndex, ruleIndex, fieldId)}
                  options={fields.map((entry) => ({ id: entry.id, label: entry.label }))}
                  searchPlaceholder="Search rule fields…"
                  popWidth={320}
                  size="sm"
                />
              );
              const remove = <span className="x" role="button" tabIndex={0} aria-label="Remove rule" onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();removeRule(groupIndex, ruleIndex);}}} onClick={() => removeRule(groupIndex, ruleIndex)}>{Icons.x}</span>;

              if (field.kind === 'production_window') {
                const value = recordValue(rule, field);
                return (
                  <RequiredField
                    key={ruleIndex}
                    fieldKey={validationKey}
                    message={validationMessage}
                    attempt={validationAttempt}
                    className="rule rule-production"
                  >
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>for</span>
                    <MultiSelect
                      order="alpha"
                      value={Array.isArray(value.productionIds) ? value.productionIds.map(String) : []}
                      onChange={(productionIds) => updateRule(groupIndex, ruleIndex, {
                        value: {
                          ...value,
                          productionIds,
                        },
                      })}
                      options={productionOptions.map((entry) => ({
                        id: entry.id,
                        label: entry.name || entry.id,
                        sublabel: productionOptionSublabel(entry, venueNames),
                      }))}
                      placeholder={productionOptions.length ? '— Select productions —' : 'No productions available'}
                      searchPlaceholder={`Search ${productionOptions.length} productions…`}
                      popWidth={520}
                      size="sm"
                    />
                    {remove}
                    <div className="rule-window-line">
                      <span className="muted mono" style={{fontSize:11}}>bought</span>
                      <PurchaseWindowPicker
                        label="Production purchased"
                        value={value.window}
                        onChange={(window) => updateRule(groupIndex, ruleIndex, {value:{...value,window}})}
                      />
                    </div>
                  </RequiredField>
                );
              }

              if (field.kind === 'purchase_window') {
                return (
                  <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule rule-window">
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                    <PurchaseWindowPicker
                      label={field.label}
                      value={rule.value}
                      lapsedNote
                      onChange={(window) => updateRule(groupIndex, ruleIndex, {value:window})}
                    />
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'bands') {
                const legacy = isLegacyRangeValue(rule.value) ? rule.value : null;
                const selected = Array.isArray(rule.value) ? rule.value.map(String) : [];
                return (
                  <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule">
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                    <MultiSelect
                      order="ranked"
                      ariaLabel={field.label}
                      value={selected}
                      onChange={(bands) => updateRule(groupIndex, ruleIndex, {value:bands})}
                      options={dropdownOptions(field.vals)}
                      // A legacy range that no set of bands spells stays as it was until a band
                      // is picked; opening the audience never edits it.
                      placeholder={legacy ? legacyRangeLabel(legacy) : '— Select bands —'}
                      searchPlaceholder={`Search ${field.label.toLowerCase()} bands…`}
                      popWidth={300}
                      size="sm"
                    />
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'frequency_window') {
                const value = recordValue(rule, field);
                return (
                  <RequiredField
                    key={ruleIndex}
                    fieldKey={validationKey}
                    message={validationMessage}
                    attempt={validationAttempt}
                    className="rule"
                    style={{gridTemplateColumns:'minmax(160px,1fr) auto minmax(120px,140px) auto minmax(120px,140px) 20px'}}
                  >
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>at least</span>
                    <SearchableDropdown
                      value={String(value.minPurchases)}
                      onChange={(count) => updateRule(groupIndex, ruleIndex, {value:{...value,minPurchases:Number(count)}})}
                      options={[2, 3, 5, 10].map((count) => ({ id: String(count), label: `${count} purchases` }))}
                      searchPlaceholder="Search purchase counts…"
                      popWidth={220}
                      size="sm"
                    />
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>in last</span>
                    <SearchableDropdown
                      value={textOf(value.window)}
                      onChange={(window) => updateRule(groupIndex, ruleIndex, {value:{...value,window}})}
                      options={windowOptions(value.window)}
                      searchPlaceholder="Search time windows…"
                      popWidth={220}
                      size="sm"
                    />
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'pixel_action_window') {
                const value = recordValue(rule, field);
                const pixelId = textOf(value.pixelId);
                const selectedPixel = pixels.find((pixel) => pixel.id === pixelId);
                const pixelOptions = pixels.map((pixel) => ({
                  id: pixel.id,
                  label: pixel.description || pixel.id,
                  sublabel: [
                    pixel.id,
                    pixel.domains?.length
                      ? pixel.domains.join(', ')
                      : 'No domains listed',
                  ].join(' · '),
                  meta: pixel.status.toUpperCase(),
                }));
                const actionOptions = pixelId
                  ? PIXEL_RETARGETING_ACTIONS.map((action) => ({
                      id: action,
                      label: action === 'Pageview' ? 'Page view' : 'Purchase',
                      sublabel: `Allowed by install instructions for ${selectedPixel?.description || pixelId}`,
                    }))
                  : [];
                return (
                  <RequiredField
                    key={ruleIndex}
                    fieldKey={validationKey}
                    message={validationMessage}
                    attempt={validationAttempt}
                    className="rule rule-pixel-action"
                  >
                    {fieldSelect}
                    <RequiredField
                      fieldKey={`${validationKey}-pixel`}
                      message={validationMessages[`${validationKey}-pixel`]}
                      attempt={validationAttempt}
                      className="rule-control-validation"
                    >
                      <SearchableDropdown
                        value={pixelId}
                        onChange={(nextPixelId) => updateRule(groupIndex, ruleIndex, {
                          value: { ...value, pixelId: nextPixelId, action: '' },
                        })}
                        options={pixelOptions}
                        placeholder={pixels.length ? '— Select pixel —' : 'No pixels available'}
                        searchPlaceholder={`Search ${pixels.length} pixels…`}
                        popWidth={420}
                        size="sm"
                      />
                    </RequiredField>
                    <RequiredField
                      fieldKey={`${validationKey}-action`}
                      message={validationMessages[`${validationKey}-action`]}
                      attempt={validationAttempt}
                      className="rule-control-validation"
                    >
                      <SearchableDropdown
                        value={textOf(value.action)}
                        onChange={(action) => updateRule(groupIndex, ruleIndex, {
                          value: { ...value, action },
                        })}
                        options={actionOptions}
                        placeholder={pixelId ? '— Select action —' : 'Select pixel first'}
                        searchPlaceholder="Search allowed actions…"
                        popWidth={360}
                        size="sm"
                        disabled={!pixelId}
                      />
                    </RequiredField>
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>in last</span>
                    <RequiredField
                      fieldKey={`${validationKey}-window`}
                      message={validationMessages[`${validationKey}-window`]}
                      attempt={validationAttempt}
                      className="rule-control-validation"
                    >
                      <SearchableDropdown
                        value={textOf(value.window)}
                        onChange={(window) => updateRule(groupIndex, ruleIndex, {value:{...value,window}})}
                        options={windowOptions(value.window)}
                        searchPlaceholder="Search time windows…"
                        popWidth={220}
                        size="sm"
                      />
                    </RequiredField>
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'text') {
                return (
                  <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule">
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                    <input className="sel" value={String(rule.value ?? '')} placeholder={field.placeholder} onChange={(event) => updateRule(groupIndex, ruleIndex, {value:event.target.value})}/>
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'audience') {
                return (
                  <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule">
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                    <SearchableDropdown
                      value={String(rule.value ?? '')}
                      onChange={(audienceId) => updateRule(groupIndex, ruleIndex, {value:audienceId})}
                      options={seedAudiences.map((entry) => ({
                        id: entry.id,
                        label: entry.name,
                        sublabel: `${visualTypeFromAudienceType(entry.audience_type)} · ${(entry.estimated_size ?? 0).toLocaleString()} people`,
                      }))}
                      placeholder={seedAudiences.length ? '— Select saved audience —' : 'No eligible seed audiences available'}
                      searchPlaceholder={`Search ${seedAudiences.length} eligible audiences…`}
                      popWidth={440}
                      size="sm"
                    />
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'multi') {
                const selected = (Array.isArray(rule.value) ? rule.value : [rule.value].filter(Boolean)).map(String);
                return (
                  <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule">
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                    <MultiSelect
                      order={field.order}
                      value={selected}
                      onChange={(nextValues) => updateRule(groupIndex, ruleIndex, {value:nextValues})}
                      options={dropdownOptions(field.vals, field.labels)}
                      placeholder="— Select values —"
                      searchPlaceholder={`Search ${field.label.toLowerCase()} values…`}
                      popWidth={360}
                      size="sm"
                    />
                    {remove}
                  </RequiredField>
                );
              }

              if (field.kind === 'boolean') {
                return (
                  <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule">
                    {fieldSelect}
                    <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                    <SearchableDropdown
                      value={String(rule.value)}
                      onChange={(nextValue) => updateRule(groupIndex, ruleIndex, {
                        value: nextValue === 'true',
                      })}
                      options={dropdownOptions(field.vals, field.labels)}
                      searchPlaceholder="Search values…"
                      popWidth={220}
                      size="sm"
                    />
                    {remove}
                  </RequiredField>
                );
              }

              return (
                <RequiredField key={ruleIndex} fieldKey={validationKey} message={validationMessage} attempt={validationAttempt} className="rule">
                  {fieldSelect}
                  <span className="muted mono" style={{fontSize:11,textAlign:'center'}}>{rule.operator}</span>
                  <SearchableDropdown
                    value={String(rule.value ?? '')}
                    onChange={(nextValue) => updateRule(groupIndex, ruleIndex, {value:nextValue})}
                    options={dropdownOptions([...new Set([String(rule.value ?? ''), ...field.vals])].filter(Boolean))}
                    searchPlaceholder={`Search ${field.label.toLowerCase()} values…`}
                    popWidth={280}
                    size="sm"
                  />
                  {remove}
                </RequiredField>
              );
            })}
            <div className="flex gap-2 mt-2">
              <button className="btn btn-ghost btn-sm" disabled={group.rules.length >= fields.length} onClick={() => addRule(groupIndex)}>
                {Icons.plus} Add rule
              </button>
            </div>
          </RequiredField>
        </Fragment>
      ))}
      {canAddAudienceGroup(type) && (
        <div className="flex gap-2 mt-2">
          <button className="btn btn-sm" onClick={addGroup}>{Icons.plus} Add group</button>
        </div>
      )}
    </RequiredField>
  );
}
